import { useCallback, useState } from "react";

import toast from "react-hot-toast";

import { CheckIcon, PencilIcon, PlayIcon } from "lucide-react";

import { countries } from "simplydo/core/countries";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";

import api from "api";

const CompanyCardHeader = styled.div`
  font-size: 1em;
  color: black;
  font-weight: 600;
`;

const CompanyCardMeta = styled.div`
  font-size: 0.8em;
  color: grey;
`;

type BusinessProfileSearcherProps = {
  forType: string;
  forId: string;
  onSelect: (company: Partial<OpenAPI.Schemas["IdeaBusinessProfile"]>) => void;
  onClose?: () => void;
};

type CreditsafeSearchResult =
  OpenAPI.GET<"/verifications/companies/search">["response"]["companies"][number] & {
    phoneNumbers?: string[];
  };

export const BusinessProfileSearcher = ({
  forType,
  forId,
  onSelect,
  onClose,
}: BusinessProfileSearcherProps) => {
  const [loading, setLoading] = useState(false);

  const [searchResults, setSearchResults] = useState<CreditsafeSearchResult[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [country, setCountry] = useState("GB");
  const [companyName, setCompanyName] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Partial<
    OpenAPI.Schemas["IdeaBusinessProfile"]
  > | null>(null);

  const apiSearch = useThrottle(
    (name: string, number: string, countryCode: string) => {
      if ((!name && !number) || (number && number.length <= 1)) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      if (number.toString().replace(" ", "").length > 8) {
        toast.error(
          "Company registration is longer than the required maximum length of 8",
        );
        setIsSearching(false);
        return;
      }

      api.verifications.searchForCompanies(
        countryCode,
        number,
        name,
        forType,
        forId,
        (data) => {
          setIsSearching(false);
          setSearchResults(data.companies ?? []);
        },
        (err) => {
          setIsSearching(false);
          toast.error(err.message);
        },
      );
    },
    400,
    [],
  );

  const searchForCreditsafeCompanies = useCallback(
    (name: string, number: string, countryCode: string) => {
      if (!countryCode) {
        return;
      }
      setIsSearching(true);
      apiSearch(name, number, countryCode);
    },
    [apiSearch],
  );

  const onClickCompany = useCallback((company: CreditsafeSearchResult) => {
    setSelectedCompany((prev) =>
      prev?.creditsafeCompanyId === company.id
        ? null
        : {
            name: company.name,
            address: {
              city: company?.address?.city ?? "",
              country: company.country ?? "",
              streetAddress: company.address?.street ?? "",
              postcode: company.address?.postCode ?? "",
            },
            phoneNumber: company.phoneNumbers?.length
              ? company.phoneNumbers[0]
              : "",
            creditsafeCompanyId: company.id,
            creditsafeRegNo: company.regNo,
            vatNo: company?.vatNo?.length ? company.vatNo[0] : "",
          },
    );
  }, []);

  return (
    <Modal open={true}>
      <ModalHeader>Search for your organisation</ModalHeader>
      <ModalContent scrolling>
        <FormField required>
          <FormLabel htmlFor="organisationName">Organisation name</FormLabel>
          <Input
            id="organisationName"
            fluid
            placeholder="Type your organisation name.."
            value={companyName}
            loading={isSearching}
            onChange={(_e, { value }) => {
              const nextValue = String(value ?? "");
              setCompanyName(nextValue);
              searchForCreditsafeCompanies(nextValue, companyNumber, country);
            }}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="registrationCountry">
            Registration country (required)
          </FormLabel>
          <Dropdown
            variant="outline"
            search
            id="registrationCountry"
            value={country}
            onChange={(_e, { value }) => {
              const nextCountry = String(value ?? "");
              setCountry(nextCountry);
              searchForCreditsafeCompanies(
                companyName,
                companyNumber,
                nextCountry,
              );
            }}
            placeholder="Select one"
            options={countries.map((c) => ({
              key: c.iso2,
              value: c.iso2,
              text: c.name,
            }))}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="registrationNumber">
            Registration number
          </FormLabel>
          <Input
            id="registrationNumber"
            fluid
            value={companyNumber}
            loading={isSearching}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCompanyNumber(e.target.value);
              searchForCreditsafeCompanies(
                companyName,
                e.target.value,
                country,
              );
            }}
            placeholder="12345678"
          />
        </FormField>

        <Divider />
        <div>
          {searchResults?.length > 0 ? (
            <Grid columns={3} mobile={1}>
              {searchResults.slice(0, 10).map((company) => {
                const isSelected =
                  selectedCompany?.creditsafeCompanyId === company.id;

                return (
                  <GridColumn key={company.id}>
                    <Card
                      onClick={() => onClickCompany(company)}
                      style={{ padding: 8 }}
                      fluid
                    >
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "stretch",
                        }}
                      >
                        <div>
                          <CompanyCardHeader>{company.name}</CompanyCardHeader>
                          <CompanyCardMeta>
                            Reg No: {company.regNo}
                          </CompanyCardMeta>
                          <CompanyCardMeta>{`${company?.address?.city || "City unknown"}, ${company.country || "Country unknown"} `}</CompanyCardMeta>
                        </div>
                        <Button
                          style={{ marginTop: 5 }}
                          color={isSelected ? "secondary" : undefined}
                        >
                          <Icon icon={isSelected ? CheckIcon : undefined} />
                          {isSelected ? "Selected" : "This is my organisation"}
                        </Button>
                      </div>
                    </Card>
                  </GridColumn>
                );
              })}
            </Grid>
          ) : isSearching ? (
            <Grid columns={3} mobile={1}>
              {Array.from({ length: 6 }).map((_, index) => {
                return (
                  <GridColumn key={index}>
                    <Card style={{ padding: 8 }} fluid>
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "stretch",
                        }}
                      >
                        <Placeholder>
                          <PlaceholderLine style={{ height: 24 }} />
                          <PlaceholderLine style={{ height: 16 }} />
                          <PlaceholderLine style={{ height: 16 }} />
                          <PlaceholderLine style={{ height: 48 }} />
                        </Placeholder>
                      </div>
                    </Card>
                  </GridColumn>
                );
              })}
            </Grid>
          ) : null}
        </div>
      </ModalContent>
      <ModalActions>
        <Button
          onClick={() => {
            onClose?.();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSelect({ name: companyName || "Default business profile" });
          }}
          color={!selectedCompany ? "primary" : undefined}
        >
          <Icon icon={PencilIcon} />
          Enter details manually
        </Button>
        {selectedCompany ? (
          <Button
            loading={loading}
            onClick={() => {
              setLoading(true);
              const chosenCompany = { ...selectedCompany };
              onSelect(chosenCompany);
              setLoading(false);
            }}
            color="primary"
          >
            <Icon icon={PlayIcon} />
            Proceed with {selectedCompany.name}
          </Button>
        ) : null}
      </ModalActions>
    </Modal>
  );
};
